import { type GeoLocation } from '../../providers/LocationProvider';
import { useResponsive } from '../../providers/ResponsiveProvider';
import {
  __,
  getLocationMetadataFromMedia,
  isSupportedMediaType,
  toastError,
  type FileAcceptType,
} from '../../utils';
import ActionMenu from '../ActionMenu';
import FilesList, { type GpsUsageState } from '../FilesList';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import InfoIcon from '@mui/icons-material/InfoOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  LightBox,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  type LightBoxPreviewMedia,
  type SxProps,
  type Theme,
} from '@presov.sk/presov-ui-react';
import type { FieldValidator } from 'final-form';
import { transparentize } from 'polished';
import React from 'react';
import { Field, type FieldRenderProps } from 'react-final-form';

const HiddenFileInput: React.FC<{
  ref: React.Ref<HTMLInputElement>;
  'aria-label': string;
  name: string;
  accept: string;
  capture?: 'user' | 'environment';
  onFiles: (files: FileList) => void;
  disabled?: boolean;
}> = ({
  ref,
  'aria-label': label,
  name,
  accept,
  capture,
  onFiles,
  disabled,
}) => {
  return (
    <input
      ref={ref}
      type='file'
      accept={accept}
      capture={capture}
      aria-label={label}
      name={name}
      multiple={true}
      onChange={(e) => onFiles(e.target.files)}
      style={{ display: 'none' }}
      disabled={disabled}
    />
  );
};

type Props = {
  name: string;
  required?: boolean;
  label?: string;
  accept?: FileAcceptType[];
  disabled?: boolean;
  maxFileSizeMB?: number;
  allowCapture?: boolean;
  sx?: SxProps<Theme>;
  info?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: FieldValidator<any>;
  onMetadata?: (metadata) => void;
  onFileChange?: (files: File[]) => void;
  gpsUsageState?: Record<string, GpsUsageState>; // filename -> state
  onGpsClick?: (fileName: string, gpsLocation: GeoLocation) => void;
};

const FormMediaField: React.FC<Props> = ({
  name,
  label,
  required,
  accept = ['image/*'],
  disabled,
  maxFileSizeMB = 250,
  allowCapture,
  sx,
  info,
  validate,
  onMetadata,
  onFileChange,
  gpsUsageState,
  onGpsClick,
}) => {
  const theme = useTheme();
  const { isNotMd } = useResponsive();

  const showCapture = allowCapture && isNotMd;
  const acceptImages = React.useMemo(
    () => accept.filter((t) => /^image\//.test(t)),
    [accept],
  );
  const acceptVideos = React.useMemo(
    () => accept.filter((t) => /^video\//.test(t)),
    [accept],
  );
  const acceptPdf = accept.includes('application/pdf');

  const photoRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLInputElement>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const [captureMenu, setCaptureMenu] =
    React.useState<Record<'top' | 'left', number>>();
  const [previewMedia, setPreviewMedia] =
    React.useState<LightBoxPreviewMedia>();
  const [draggedOver, setDraggedOver] = React.useState(false);

  const handleDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    ev.dataTransfer.dropEffect = 'copy';
    setDraggedOver(true);
  };

  const handleClick = (position: Record<'top' | 'left', number>) => {
    if (showCapture) {
      setCaptureMenu(captureMenu ? undefined : position);
    } else {
      fileRef.current?.click();
    }
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    if (e.key === 'Enter') {
      handleClick({
        top: window.scrollY + bounds.top + (bounds.bottom - bounds.top) / 2,
        left: window.scrollX + bounds.left + (bounds.right - bounds.left) / 2,
      });
    }
  };

  const handleCapture = (ref: React.RefObject<HTMLInputElement>) => {
    ref.current?.click();
    setCaptureMenu(undefined);
  };

  const validateFiles = async (files: File[], totalFiles: File[]) => {
    const currentFileSizes = (totalFiles || []).reduce(
      (acc, file) => acc + file.size,
      0,
    );

    const validFiles = files.filter(({ name, size, type }) => {
      if (size === 0) {
        toastError(__(`Súbor '${name}' je prázdny.`));
      } else if (size > maxFileSizeMB * 1024 * 1024) {
        toastError(
          __(
            `Súbor '${name}' prekračuje povolenú veľkosť ${maxFileSizeMB} MB.`,
          ),
        );
      } else if (!isSupportedMediaType(accept, type)) {
        toastError(__(`Formát '${type}' súboru '${name}' nie je podporovaný.`));
      } else if (currentFileSizes + size > maxFileSizeMB * 1024 * 1024) {
        toastError(
          __(
            `Celková veľkosť súborov prekračuje povolenú veľkosť ${maxFileSizeMB} MB.`,
          ),
        );
      } else {
        return true;
      }
    });

    const filesWithLocation = await Promise.all(
      validFiles.map(async (file) => {
        const metadata = await getLocationMetadataFromMedia([file]);
        if (metadata) {
          const fileWithLocation = Object.assign(file, { location: metadata });
          return fileWithLocation;
        } else return file;
      }),
    );

    onFileChange?.(filesWithLocation);

    return filesWithLocation;
  };

  return (
    <>
      <Field name={name} validate={validate}>
        {({
          input: { name, value, onChange },
          meta: { touched, error, submitError },
        }: FieldRenderProps<File[]>) => {
          return (
            <FormControl sx={sx}>
              {label && (
                <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                  <FormLabel htmlFor={name} required={required}>
                    {label}
                  </FormLabel>
                  {info && (
                    <Tooltip title={info}>
                      <InfoIcon
                        sx={{
                          fontSize: 16,
                          mb: 0.25,
                          cursor: 'pointer',
                          color: (theme) => theme.palette.info.light,
                          ':hover': {
                            color: (theme) => theme.palette.info.main,
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              )}
              <Stack
                alignItems={'center'}
                justifyContent={'center'}
                gap={1}
                sx={{
                  height: 200,
                  cursor: 'pointer',
                  width: '100%',
                  borderRadius: 1,
                  border: `2px dashed`,
                  ':hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: transparentize(
                      0.98,
                      theme.palette.primary.main,
                    ),
                  },
                  borderColor: draggedOver
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                }}
                tabIndex={0}
                role='button'
                onKeyDown={handleEnter}
                onClick={(e) =>
                  handleClick({ top: e.clientY, left: e.clientX })
                }
                onDragOver={handleDragOver}
                onDragLeave={() => setDraggedOver(false)}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const newFiles = await validateFiles(
                    Array.from(e.dataTransfer.files),
                    value || [],
                  );
                  onChange([...(value || []), ...newFiles]);
                  setDraggedOver(false);
                }}
              >
                <UploadFileIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.text.secondary,
                  }}
                />
                <Typography
                  variant='body1'
                  color='text.primary'
                  sx={{
                    textAlign: 'center',
                    width: {
                      xs: 180,
                      md: 'unset',
                    },
                  }}
                >
                  {isNotMd
                    ? acceptPdf
                      ? __(`Pridať fotografiu, video alebo PDF`)
                      : __(`Pridať fotografiu alebo video`)
                    : __(`Presuňte súbory sem alebo kliknite pre výber`)}
                </Typography>
              </Stack>
              {isNotMd && (
                <>
                  <HiddenFileInput
                    ref={photoRef}
                    name={name}
                    aria-label={__('Odfotiť obrázok')}
                    accept={acceptImages.join(',')}
                    capture='environment'
                    onFiles={async (files) => {
                      const newFiles = await validateFiles(
                        Array.from(files),
                        value || [],
                      );
                      onChange([...(value || []), ...newFiles]);
                    }}
                    disabled={disabled}
                  />
                  <HiddenFileInput
                    ref={videoRef}
                    name={name}
                    aria-label={__('Nahrať video')}
                    accept={acceptVideos.join(',')}
                    capture='environment'
                    onFiles={async (files) => {
                      const newFiles = await validateFiles(
                        Array.from(files),
                        value || [],
                      );
                      onChange([...(value || []), ...newFiles]);
                    }}
                    disabled={disabled}
                  />
                </>
              )}
              <HiddenFileInput
                ref={fileRef}
                aria-label={__('Nahrať súbory')}
                name={name}
                accept={accept.join(',')}
                onFiles={async (files) => {
                  const newFiles = await validateFiles(
                    Array.from(files),
                    value || [],
                  );
                  onChange([...(value || []), ...newFiles]);
                }}
                disabled={disabled}
              />
              <ActionMenu
                position={captureMenu}
                onClose={() => setCaptureMenu(undefined)}
                items={[
                  {
                    key: 'photo',
                    Icon: AddAPhotoIcon,
                    title: __('Odfotiť obrázok'),
                    onClick: () => handleCapture(photoRef),
                  },
                  {
                    key: 'video',
                    Icon: VideoCameraBackIcon,
                    title: __('Nahrať video'),
                    onClick: () => handleCapture(videoRef),
                  },
                  {
                    key: 'file',
                    Icon: UploadFileIcon,
                    title: __('Nahrať súbor'),
                    onClick: () => handleCapture(fileRef),
                  },
                ]}
              />
              <Stack justifyContent={'space-between'} direction='row' mt={1}>
                <Stack
                  direction={'row'}
                  spacing={2}
                  flex={1}
                  justifyContent={'flex-end'}
                >
                  <Typography variant='caption' color='text.secondary'>
                    {__(
                      `Celková veľkosť: ${value ? Math.floor(value.reduce((acc, file) => acc + file.size, 0) / 1024 / 1024) : 0} / ${maxFileSizeMB} MB`,
                    )}
                  </Typography>
                </Stack>
              </Stack>

              {touched && (error || submitError) && (
                <FormHelperText
                  error
                  sx={{
                    height: 20,
                  }}
                >
                  {error || submitError || ''}
                </FormHelperText>
              )}

              {!!value?.length && (
                <FilesList
                  sx={{ mt: 1.5 }}
                  files={value || []}
                  onPreview={(j) => {
                    const file = (value || [])[j];
                    if (file && /(image|video)\//.test(file.type)) {
                      setPreviewMedia({
                        url: URL.createObjectURL(file),
                        type: file.type,
                      });
                    } else if (file) {
                      window.open(URL.createObjectURL(file));
                    }
                  }}
                  onRemove={(j) => {
                    const newFiles = (value || []).filter((_, i) => i !== j);
                    onChange(newFiles);
                  }}
                  onLocate={(position) => {
                    onMetadata?.(position);
                  }}
                  gpsUsageState={gpsUsageState}
                  onGpsClick={onGpsClick}
                />
              )}
            </FormControl>
          );
        }}
      </Field>
      <LightBox
        media={previewMedia}
        onClose={() => setPreviewMedia(undefined)}
      />
    </>
  );
};

export default FormMediaField;
